// Markdown body for the post. Metadata lives in src/blog/posts.json.
const body = `
Most people start with the scratch tests and the acid kits. Start with the mark instead. On the great majority of jewelry made in the last two hundred years, the metal tells you what it is, in tiny letters, if you know where to look.

## Where the mark hides

Marks are stamped where they will not be seen when the piece is worn.

- **Rings**: inside the shank, usually at the bottom, opposite the stone.
- **Chains and bracelets**: on the clasp, on the small tag next to the clasp, or on the last link.
- **Brooches**: on the back plate, on the pin stem, or on the hinge.
- **Earrings**: on the post, on the butterfly back, or on the clip mechanism.
- **Pendants**: on the bail, the small loop the chain runs through.

Use daylight and magnification. A cheap 10x loupe works, and so does the macro mode on a modern phone. Old marks are often half worn away, so photograph the area and zoom into the photograph rather than squinting at the piece.

## What the numbers mean

Modern marks state purity in parts per thousand. Older and British and American marks often state karats instead. They are the same information in two dialects.

- **999 or 24k**: pure gold. Soft, deep yellow, rare in wearable jewelry.
- **916 or 22k**: 91.6 percent gold. Common in Indian and Middle Eastern jewelry.
- **750 or 18k**: 75 percent gold. The standard for fine European jewelry.
- **585 or 14k**: 58.5 percent gold. Very common in American and German pieces.
- **417 or 10k**: 41.7 percent gold. The legal minimum in the United States.
- **375 or 9k**: 37.5 percent gold. The British and Irish workhorse. Scandinavia does not use it much: Danish everyday gold is 585 and Swedish is usually 750.

You will also meet purity marks for other metals. 925 is sterling silver, 800 is a lower continental silver standard, and 950 or PT950 is platinum. None of these are gold, but all of them are solid precious metal and worth keeping.

## The marks that mean it is not solid gold

These are the letters that change the value of a piece by a factor of ten or more.

- **GF or 1/20 12K GF**: gold filled. A layer of real gold mechanically bonded over a base metal core. Better than plating, still not solid.
- **GP, HGE or GEP**: gold plated, heavy gold electroplate, gold electroplated. A microscopically thin coating.
- **RGP**: rolled gold plate. Thin, bonded, old fashioned term.
- **EPNS**: electroplated nickel silver. No silver and no gold anywhere in it.
- **Vermeil**: sterling silver with a gold layer on top. The silver underneath has real value, the gold layer is thin.

If you see any of these, stop worrying about acid tests. The mark has already told you.

## British assay marks are a gift

If a piece was assayed in Britain, it carries a full set of marks rather than a single number: a sponsor mark for the maker, a standard mark for the purity, a town mark for the assay office, and a date letter that gives the exact year. A lion passant means sterling silver, a crown was historically used for gold. The date letter changes shape and typeface every cycle, so a reference table will take you to a single year.

That level of detail is unusual. Most other countries mark purity and maker, and leave you to date the piece from its style. If you want to work that out, our [era by era guide to dating antique jewelry](/blog/how-to-date-antique-jewelry-by-era) covers the clues that do not depend on marks.

## When there is no mark at all

An unmarked piece is not automatically fake. There are ordinary reasons for a blank shank.

- Hallmarking was never compulsory in some countries, including much of the United States before about 1906.
- The mark sat on the part of the shank that was cut out when the ring was resized.
- Fifty years of wear against a finger simply erased it.
- The piece is older than the marking system that would have covered it.

For unmarked pieces, judge the metal by colour and behaviour, the construction by how it was made, and the stones by how they were cut. Then confirm it properly rather than guessing.

## Home checks that do no damage

- **The magnet check**: gold, silver and platinum are not magnetic. If a strong magnet pulls at the piece, there is iron or steel inside it. This rules things out, it never confirms them, because brass and copper are not magnetic either.
- **Look at the wear points**: the edges of a ring, the high points of a pattern, the back of a clasp. If a different colour shows through where the piece rubs, you are looking at plating wearing off a base metal core.
- **Look for corrosion**: solid gold does not rust, pit or flake. Green crusting or reddish spots mean copper or brass is present at the surface.
- **Weigh it in your hand**: gold is dense. A solid 18k ring feels distinctly heavier than a plated one of the same size. This is a feel you develop after handling a few, not a test on its own.

## The checks worth skipping

Do not file a notch into the piece. Do not drag it across a ceramic tile. Do not buy an acid kit and start dripping. All three damage the surface, all three are easy to misread, and all three can destroy far more value than the answer is worth, especially on an antique.

If you want certainty, take the piece to a jeweller or a pawnbroker with an XRF analyser. It is a handheld scanner that reads the alloy through the surface in a few seconds, it touches nothing, and most shops will do it free or for a very small fee. Ask for the reading on more than one spot, because a plated piece can read differently on a thick area.

## Buying gold jewelry from a stranger

If you are on the buying side, the same marks are your first filter, and a few habits protect the rest.

- Ask for a photograph of the mark itself, not a description of it. A seller who cannot produce one has not looked.
- Ask what the piece weighs. Weight plus purity gives you the metal floor under the price.
- Read the condition notes for repairs, resizes and replaced stones, and ask if they are missing.
- Pay through the platform. If the money is held until the piece arrives and you have confirmed it, a mistake is recoverable. If you sent a bank transfer to a stranger, it is not.

Every piece on Nextmine is sold that way by default: the buyer's payment is held by Stripe until the piece has arrived and been confirmed, and the seller is never paid before then. You can read the mechanics on our [how payments work](/how-payments-work) page, or [browse what is listed right now](/s).

If you have a mark you cannot place, the Hallmark Society's [hallmark decoder](https://hallmarksociety.com/tools/hallmark-decoder/) looks up fineness numbers, assay office marks and country marks.
`;

export default body;
